import { authMiddlewares } from '@/middlewares/auth.middlewares';
import { ecommerceMiddlewares } from '@/middlewares/ecommerce.middlewares';
import { i18n } from '@/plugins/i18n';
import adminRoutes from '@/modules/admin/routes/admin.routes';
import npsRoutes from '@/modules/nps/routes/nps.routes';
import { useAuthStore } from '@/stores/auth.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import type { RouteRecordRaw } from 'vue-router';

const sharedRoutes: RouteRecordRaw[] = [
  {
    path: '/user/settings',
    component: () => import('@/layouts/UserSettingsLayout.vue'),
    beforeEnter: [authMiddlewares.isAuthenticated, authMiddlewares.isAccountConfigured],
    children: [
      {
        path: 'data',
        name: 'UserSettingsDataView',
        meta: { title: 'user_settings.nav_menu.personal_data' },
        component: () => import('@/views/user-settings/UserSettingsDataView.vue'),
      },
      {
        path: 'security',
        name: 'UserSettingsSecurityView',
        meta: { title: 'user_settings.nav_menu.security' },
        component: () => import('@/views/user-settings/UserSettingsSecurityView.vue'),
      },
    ],
  },
  {
    path: '/account/settings',
    component: () => import('@/layouts/AccountSettingsLayout.vue'),
    beforeEnter: [authMiddlewares.isAuthenticated, authMiddlewares.isAccountConfigured],
    children: [
      {
        path: 'general',
        name: 'AccountSettingsGeneralView',
        meta: { title: 'account_settings.nav_menu.general' },
        component: () => import('@/views/account-settings/AccountSettingsGeneralView.vue'),
      },
      {
        path: 'users',
        name: 'AccountSettingsUsersView',
        meta: { title: 'account_settings.nav_menu.users' },
        component: () => import('@/views/account-settings/AccountSettingsUsersView.vue'),
      },
      {
        path: 'billing',
        name: 'AccountSettingsBillingView',
        meta: { title: 'account_settings.nav_menu.billing' },
        component: () => import('@/views/account-settings/AccountSettingsBillingView.vue'),
      },
      {
        path: 'plan',
        meta: { title: 'account_settings.nav_menu.plan' },
        beforeEnter: [
          (to, from, next) => {
            const orderSummaryStore = useOrderSummaryStore();
            if (orderSummaryStore.hasFreePlanActive) {
              next({ name: 'InvestView' });
            }

            next();
          },
        ],
        children: [
          {
            path: '',
            name: 'AccountSettingsPlanView',
            component: () => import('@/views/account-settings/AccountSettingsPlanView.vue'),
          },
          {
            path: 'change',
            name: 'AccountSettingsPlanChangeView',
            component: () => import('@/views/account-settings/AccountSettingsPlanChangeView.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/cart',
    component: () => import('@/layouts/BlankLayout.vue'),
    redirect: '/cart/select',
    beforeEnter: [
      authMiddlewares.isAuthenticated,
      (to, from, next) => {
        const orderSummaryStore = useOrderSummaryStore();
        if (!orderSummaryStore.hasFreePlanActive) return next({ name: 'AccountSettingsPlanView' });
        return next();
      },
      (to, from, next) => {
        const authStore = useAuthStore();
        if (!authStore.isFlokeeAccount) return next();

        ecommerceMiddlewares.showPlanSubscriptionApprovalModal(to, from, next);
      },
    ],
    children: [
      {
        path: 'select',
        component: () => import('@/layouts/BaseLayout.vue'),
        children: [
          {
            path: '',
            meta: { title: 'cart_select.meta_title' },
            name: 'CartSelectView',
            props: (route) => ({
              coupon: route.query.coupon ?? '',
            }),
            component: () => import('@/views/cart/CartSelectView.vue'),
          },
        ],
      },
      {
        path: 'checkout/:cart_id',
        name: 'CartCheckoutView',
        meta: { title: 'cart_checkout.title' },
        props: (route) => ({
          cart_id: Number(route.params.cart_id),
          coupon: route.query.coupon ?? '',
        }),
        component: () => import('@/views/cart/CartCheckoutView.vue'),
      },
    ],
  },
  {
    path: '/test',
    component: () => import('@/layouts/BaseLayout.vue'),
    beforeEnter: [authMiddlewares.verifyAuthentication],
    children: [
      {
        path: 'pusher',
        name: 'TestPusherView',
        component: () => import('@/views/test/TestPusherView.vue'),
      },
    ],
  },
  {
    path: '/admin',
    meta: { title: 'Admin' },
    beforeEnter: [authMiddlewares.verifyAuthentication, authMiddlewares.isAdmin],
    children: [...adminRoutes],
  },
  {
    path: '/nps',
    meta: { title: 'nps.meta_title' },
    children: [...npsRoutes],
  },
  {
    path: '/terms',
    component: () => import('@/layouts/TermsLayout.vue'),
    beforeEnter: [
      (to, from, next) => {
        if (to.path === '/terms') {
          const { locale } = i18n.global as any;
          if (locale.value === 'es') {
            return next({ name: 'TermsEsView' });
          } else if (locale.value === 'pt') {
            return next({ name: 'TermsPtView' });
          } else {
            return next({ name: 'TermsEnView' });
          }
        }
        next();
      },
    ],
    children: [
      {
        path: 'en',
        name: 'TermsEnView',
        component: () => import('@/views/terms/TermsEnView.vue'),
      },
      {
        path: 'es',
        name: 'TermsEsView',
        component: () => import('@/views/terms/TermsEsView.vue'),
      },
      {
        path: 'pt',
        name: 'TermsPtView',
        component: () => import('@/views/terms/TermsPtView.vue'),
      },
    ],
  },
  {
    path: '/auth/:pathMatch(.*)*',
    redirect: (to) => `/flokee/auth/${to.params.pathMatch ?? ''}`,
  },
  {
    path: '/wizard/:pathMatch(.*)*',
    redirect: (to) => `/flokee/wizard/${to.params.pathMatch ?? ''}`,
  },
  {
    path: '/widgets/:pathMatch(.*)*',
    redirect: (to) => `/flokee/widgets/${to.params.pathMatch ?? ''}`,
  },
  {
    path: '/store/settings/:pathMatch(.*)*',
    redirect: (to) => `/flokee/store/settings/${to.params.pathMatch ?? ''}`,
  },
  {
    path: '/store/connections/:pathMatch(.*)*',
    redirect: (to) => `/flokee/store/connections/${to.params.pathMatch ?? ''}`,
  },
  {
    path: '/store/:pathMatch(.*)*',
    redirect: (to) => `/flokee/store/${to.params.pathMatch ?? ''}`,
  },
  {
    path: '/invest',
    redirect: '/flokee/invest',
  },
  {
    path: '/select-account',
    redirect: '/flokee/select-account',
  },
  {
    path: '/geo',
    redirect: '/cleepo',
    children: [
      {
        path: ':pathMatch(.*)*',
        redirect: '/cleepo',
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    meta: { title: 'not_found.title' },
    component: () => import('@/layouts/BaseLayout.vue'),
    beforeEnter: [authMiddlewares.verifyAuthentication],
    children: [
      {
        path: '',
        name: 'NotFoundView',
        component: () => import('@/views/common/NotFoundView.vue'),
      },
    ],
  },
];

export default sharedRoutes;
